import { useEffect, useMemo, useRef, useState } from 'react';
import type { PlayerPublic, RollEvent } from '../../../shared/types';
import type { Cell, Color } from '../../../shared/board';
import {
  hopSquares,
  LADDER_LIST,
  LADDERS,
  sampleCubic,
  SNAKES,
  SNAKE_STYLES,
  snakeControls,
  squareToCell,
} from '../../../shared/board';
import { playClimb, playHop, playSlide } from '../sound';

const SIZE = 760;
const ORIGIN = 50;
const CELL = 66;
const STEP_MS = 210;
const SLIDE_MS = 720;

const WASH = [
  { fill: '#ff8b7a', num: '#7a2218' },
  { fill: '#ffd166', num: '#6b4a08' },
  { fill: '#8ce99a', num: '#14532d' },
  { fill: '#74c0fc', num: '#1c3d5a' },
  { fill: '#e599f7', num: '#5b2160' },
  { fill: '#fff3bf', num: '#5c4a12' },
];

const TOKEN: Record<Color, string> = {
  red: '#ff5a5a',
  green: '#35d07f',
  yellow: '#ffce3d',
  blue: '#4aa8ff',
};
const TOKEN_LIGHT: Record<Color, string> = {
  red: '#ff8790',
  green: '#7ee7ac',
  yellow: '#ffe58a',
  blue: '#8fc8ff',
};
const TOKEN_DARK: Record<Color, string> = {
  red: '#d92b3a',
  green: '#1fb663',
  yellow: '#e8a916',
  blue: '#2d82e0',
};

interface BoardProps {
  players: PlayerPublic[];
  currentColor: Color | null;
  lastRoll: RollEvent | null;
  myColor: Color | null;
}

interface Motion {
  color: Color;
  path: Cell[];
  hopLen: number;
  startedAt: number;
  kind: 'hop' | 'climb' | 'slide';
}

function px(c: Cell): Cell {
  return { x: ORIGIN + c.x * CELL + CELL / 2, y: ORIGIN + c.y * CELL + CELL / 2 };
}

function squarePx(n: number): Cell {
  return px(squareToCell(n));
}

function startDock(index: number, total: number): Cell {
  const spread = 28;
  const mid = (total - 1) / 2;
  return {
    x: ORIGIN + CELL / 2 + (index - mid) * spread,
    y: SIZE - 22,
  };
}

function rollKey(r: RollEvent | null): string {
  if (!r) return '';
  return `${r.id}:${r.color}:${r.from}:${r.via}:${r.to}:${r.value}:${r.forfeited}:${r.teleport ?? ''}`;
}

function lerpPath(path: Cell[], t: number): Cell {
  if (path.length === 0) return { x: 0, y: 0 };
  if (path.length === 1 || t <= 0) return path[0];
  if (t >= 1) return path[path.length - 1];
  const f = t * (path.length - 1);
  const i = Math.min(path.length - 2, Math.floor(f));
  const u = f - i;
  return {
    x: path[i].x + (path[i + 1].x - path[i].x) * u,
    y: path[i].y + (path[i + 1].y - path[i].y) * u,
  };
}

export default function Board({ players, currentColor, lastRoll, myColor }: BoardProps) {
  const [motion, setMotion] = useState<Motion | null>(null);
  const [sparkle, setSparkle] = useState<Cell | null>(null);
  const motionRef = useRef(motion);
  motionRef.current = motion;
  const lastKeyRef = useRef('');
  const hopTickRef = useRef(-1);
  const nowRef = useRef(performance.now());

  useEffect(() => {
    const key = rollKey(lastRoll);
    if (!lastRoll || key === lastKeyRef.current) return;
    if (lastRoll.forfeited || (lastRoll.from === lastRoll.to && lastRoll.overshot === false && !lastRoll.teleport)) {
      lastKeyRef.current = key;
      return;
    }
    lastKeyRef.current = key;

    const parkedAtStart = players.filter((p) => p.position === 0 || p.color === lastRoll.color);
    const dockI = Math.max(0, parkedAtStart.findIndex((p) => p.color === lastRoll.color));

    const hop: Cell[] = [];
    if (lastRoll.from <= 0) hop.push(startDock(dockI, Math.max(parkedAtStart.length, 1)));
    else hop.push(squarePx(lastRoll.from));
    for (const n of hopSquares(Math.max(lastRoll.from, 0), lastRoll.via)) hop.push(squarePx(n));

    let rest: Cell[] = [];
    let kind: Motion['kind'] = 'hop';
    if (lastRoll.teleport === 'snake') {
      const style = SNAKE_STYLES.find((s) => s.from === lastRoll.via);
      if (style) {
        const c = snakeControls(style);
        rest = sampleCubic(squareToCell(style.from), c.c1, c.c2, squareToCell(style.to), 18).map(px);
        kind = 'slide';
      }
    } else if (lastRoll.teleport === 'ladder') {
      const a = squarePx(lastRoll.via);
      const b = squarePx(lastRoll.to);
      const steps = 12;
      rest = Array.from({ length: steps + 1 }, (_, i) => ({
        x: a.x + ((b.x - a.x) * i) / steps,
        y: a.y + ((b.y - a.y) * i) / steps,
      }));
      kind = 'climb';
    }

    const path = rest.length > 1 ? [...hop, ...rest.slice(1)] : hop;
    hopTickRef.current = -1;
    setMotion({
      color: lastRoll.color,
      path,
      hopLen: hop.length,
      startedAt: performance.now(),
      kind,
    });
  }, [lastRoll, players]);

  useEffect(() => {
    if (!motion) return;
    let raf = 0;
    const hopMs = Math.max(1, motion.hopLen - 1) * STEP_MS;
    const slideMs = motion.path.length > motion.hopLen ? SLIDE_MS : 0;
    const total = hopMs + slideMs;

    const tick = () => {
      nowRef.current = performance.now();
      const elapsed = nowRef.current - motion.startedAt;
      if (elapsed < hopMs && motion.hopLen > 1) {
        const box = Math.floor(elapsed / STEP_MS);
        if (box !== hopTickRef.current && box < motion.hopLen - 1) {
          hopTickRef.current = box;
          playHop(box);
        }
      }
      if (elapsed >= hopMs && slideMs > 0 && hopTickRef.current < 1000) {
        hopTickRef.current = 1000;
        if (motion.kind === 'climb') playClimb();
        if (motion.kind === 'slide') playSlide();
      }
      if (elapsed >= total) {
        const end = motion.path[motion.path.length - 1];
        setSparkle(end);
        setMotion(null);
        window.setTimeout(() => setSparkle((s) => (s === end ? null : s)), 700);
        return;
      }
      setMotion((m) => (m ? { ...m } : m));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [motion?.startedAt]);

  const parked = players.filter((p) => p.position === 0 && (!motion || motion.color !== p.color));
  const onBoard = players.filter((p) => p.position > 0 && (!motion || motion.color !== p.color));
  const groups = new Map<number, PlayerPublic[]>();
  for (const p of onBoard) {
    const arr = groups.get(p.position) ?? [];
    arr.push(p);
    groups.set(p.position, arr);
  }

  const movingPos = useMemo(() => {
    if (!motion) return null;
    const elapsed = nowRef.current - motion.startedAt;
    const hopMs = Math.max(1, motion.hopLen - 1) * STEP_MS;
    const slideMs = motion.path.length > motion.hopLen ? SLIDE_MS : 0;
    const hopPath = motion.path.slice(0, motion.hopLen);
    const slidePath = motion.path.slice(Math.max(0, motion.hopLen - 1));
    if (elapsed < hopMs || slidePath.length < 2) {
      return lerpPath(hopPath, hopMs <= 0 ? 1 : elapsed / hopMs);
    }
    return lerpPath(slidePath, Math.min(1, (elapsed - hopMs) / slideMs));
  }, [motion]);

  const squares = [];
  for (let n = 1; n <= 100; n++) {
    const { x, y } = squareToCell(n);
    const wash = WASH[(n - 1) % WASH.length];
    const isHead = SNAKES[n] != null;
    const isFoot = LADDERS[n] != null;
    squares.push(
      <g key={n}>
        <rect
          x={ORIGIN + x * CELL + 1.5}
          y={ORIGIN + y * CELL + 1.5}
          width={CELL - 3}
          height={CELL - 3}
          rx={8}
          fill={n === 100 ? '#ffd166' : n === 1 ? '#fff3bf' : wash.fill}
        />
        <rect
          x={ORIGIN + x * CELL + 1.5}
          y={ORIGIN + y * CELL + 1.5}
          width={CELL - 3}
          height={CELL * 0.38}
          rx={8}
          fill="rgba(255,255,255,0.22)"
        />
        {isHead && (
          <rect
            x={ORIGIN + x * CELL + 1.5}
            y={ORIGIN + y * CELL + 1.5}
            width={CELL - 3}
            height={CELL - 3}
            rx={8}
            fill="rgba(224,49,49,0.22)"
          />
        )}
        {isFoot && (
          <rect
            x={ORIGIN + x * CELL + 1.5}
            y={ORIGIN + y * CELL + 1.5}
            width={CELL - 3}
            height={CELL - 3}
            rx={8}
            fill="rgba(47,158,68,0.22)"
          />
        )}
        <text
          x={ORIGIN + x * CELL + 8}
          y={ORIGIN + y * CELL + 18}
          fontFamily="Fredoka, ui-rounded, system-ui, sans-serif"
          fontSize={n === 100 ? 13 : 12}
          fontWeight={700}
          fill={n === 100 ? '#6b4a08' : wash.num}
        >
          {n}
        </text>
      </g>,
    );
  }

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="h-full w-full drop-shadow-[0_28px_48px_-12px_rgba(0,0,0,0.65)]">
      <defs>
        {(Object.keys(TOKEN) as Color[]).map((c) => (
          <radialGradient key={c} id={`snk-token-${c}`} cx="0.36" cy="0.3" r="0.95" fx="0.3" fy="0.22">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="30%" stopColor={TOKEN_LIGHT[c]} />
            <stop offset="62%" stopColor={TOKEN[c]} />
            <stop offset="100%" stopColor={TOKEN_DARK[c]} />
          </radialGradient>
        ))}
        <radialGradient id="snk-wood" cx="0.5" cy="0.2" r="1.1">
          <stop offset="0%" stopColor="#6b4423" />
          <stop offset="55%" stopColor="#4a2c14" />
          <stop offset="100%" stopColor="#2c1708" />
        </radialGradient>
        <linearGradient id="snk-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffe9a8" />
          <stop offset="50%" stopColor="#f5c23f" />
          <stop offset="100%" stopColor="#d99b1f" />
        </linearGradient>
        <linearGradient id="snk-rail" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f3d19a" />
          <stop offset="45%" stopColor="#d4a056" />
          <stop offset="100%" stopColor="#a56b2b" />
        </linearGradient>
        <filter id="snk-soft" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="2.2" stdDeviation="2" floodColor="rgba(0,0,0,0.45)" />
        </filter>
        {SNAKE_STYLES.map((s, i) => (
          <linearGradient key={i} id={`snk-skin-${i}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={s.light} />
            <stop offset="45%" stopColor={s.fill} />
            <stop offset="100%" stopColor={s.dark} />
          </linearGradient>
        ))}
      </defs>

      <rect x="8" y="8" width={SIZE - 16} height={SIZE - 16} rx="28" fill="url(#snk-wood)" />
      <rect x="20" y="20" width={SIZE - 40} height={SIZE - 40} rx="22" fill="none" stroke="url(#snk-gold)" strokeWidth="6" />
      <rect x={ORIGIN - 6} y={ORIGIN - 6} width={CELL * 10 + 12} height={CELL * 10 + 12} rx="10" fill="#2a1840" />

      {squares}

      {LADDER_LIST.map((l) => (
        <Ladder key={`l${l.from}`} from={l.from} to={l.to} />
      ))}

      {SNAKE_STYLES.map((s, i) => (
        <Snake key={`s${s.from}`} style={s} index={i} />
      ))}

      {parked.map((p, i) => {
        const pos = startDock(i, parked.length);
        return <TokenMarble key={p.id} color={p.color} x={pos.x} y={pos.y} glow={p.color === currentColor} mine={p.color === myColor} />;
      })}

      {[...groups.entries()].map(([sq, group]) => {
        const c = squarePx(sq);
        return group.map((p, i) => {
          const fan = (i - (group.length - 1) / 2) * 12;
          return (
            <TokenMarble
              key={p.id}
              color={p.color}
              x={c.x + fan}
              y={c.y}
              glow={p.color === currentColor}
              mine={p.color === myColor}
            />
          );
        });
      })}

      {motion && movingPos && (
        <TokenMarble color={motion.color} x={movingPos.x} y={movingPos.y} glow hopping mine={motion.color === myColor} />
      )}

      {sparkle && (
        <g className="snk-sparkle" pointerEvents="none">
          {[0, 45, 90, 135].map((deg) => (
            <rect
              key={deg}
              x={sparkle.x - 2}
              y={sparkle.y - 14}
              width={4}
              height={10}
              rx={2}
              fill="#ffe9a8"
              transform={`rotate(${deg} ${sparkle.x} ${sparkle.y})`}
            />
          ))}
          <circle cx={sparkle.x} cy={sparkle.y} r="5" fill="#fff" opacity="0.9" />
        </g>
      )}
    </svg>
  );
}

function TokenMarble({
  color,
  x,
  y,
  glow,
  hopping,
  mine,
}: {
  color: Color;
  x: number;
  y: number;
  glow?: boolean;
  hopping?: boolean;
  mine?: boolean;
}) {
  const r = 15;
  return (
    <g className={hopping ? 'snk-hop' : undefined} filter="url(#snk-soft)" style={{ pointerEvents: 'none' }}>
      {glow && <circle cx={x} cy={y} r={r + 7} fill={TOKEN[color]} opacity="0.28" />}
      <circle cx={x} cy={y + 2} r={r} fill="rgba(0,0,0,0.25)" />
      <circle cx={x} cy={y} r={r} fill={`url(#snk-token-${color})`} stroke="rgba(255,255,255,0.45)" strokeWidth="1.4" />
      <circle cx={x - 4} cy={y - 5} r={4.5} fill="rgba(255,255,255,0.55)" />
      {mine && <circle cx={x} cy={y} r={r + 3} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeDasharray="3 3" />}
    </g>
  );
}

function Ladder({ from, to }: { from: number; to: number }) {
  const a = squarePx(from);
  const b = squarePx(to);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const half = 9;
  const r1a = { x: a.x + px * half, y: a.y + py * half };
  const r1b = { x: b.x + px * half, y: b.y + py * half };
  const r2a = { x: a.x - px * half, y: a.y - py * half };
  const r2b = { x: b.x - px * half, y: b.y - py * half };
  const rungs = [];
  const count = Math.max(3, Math.round(len / 22));
  for (let i = 1; i < count; i++) {
    const t = i / count;
    rungs.push({
      x1: r1a.x + (r1b.x - r1a.x) * t,
      y1: r1a.y + (r1b.y - r1a.y) * t,
      x2: r2a.x + (r2b.x - r2a.x) * t,
      y2: r2a.y + (r2b.y - r2a.y) * t,
    });
  }
  return (
    <g opacity="0.95">
      <line x1={r1a.x} y1={r1a.y} x2={r1b.x} y2={r1b.y} stroke="url(#snk-rail)" strokeWidth="5.5" strokeLinecap="round" />
      <line x1={r2a.x} y1={r2a.y} x2={r2b.x} y2={r2b.y} stroke="url(#snk-rail)" strokeWidth="5.5" strokeLinecap="round" />
      {rungs.map((r, i) => (
        <line key={i} {...r} stroke="#f0d19a" strokeWidth="4" strokeLinecap="round" />
      ))}
      <circle cx={a.x} cy={a.y} r="4.5" fill="#ffe9a8" stroke="#d99b1f" strokeWidth="1.2" />
      <circle cx={b.x} cy={b.y} r="4.5" fill="#ffe9a8" stroke="#d99b1f" strokeWidth="1.2" />
    </g>
  );
}

function Snake({ style, index }: { style: (typeof SNAKE_STYLES)[number]; index: number }) {
  const p0 = squarePx(style.from);
  const p3 = squarePx(style.to);
  const c = snakeControls(style);
  const c1 = px(c.c1);
  const c2 = px(c.c2);
  const d = `M ${p0.x} ${p0.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${p3.x} ${p3.y}`;
  const ang = Math.atan2(c1.y - p0.y, c1.x - p0.x);
  const back = ang + Math.PI;
  const eyeOff = 5;
  const perp = ang + Math.PI / 2;
  return (
    <g>
      <path d={d} fill="none" stroke={`url(#snk-skin-${index})`} strokeWidth="16" strokeLinecap="round" />
      <path d={d} fill="none" stroke={style.light} strokeWidth="6" strokeLinecap="round" opacity="0.45" />
      <path d={d} fill="none" stroke={style.dark} strokeWidth="16" strokeLinecap="round" strokeDasharray="3 11" opacity="0.28" />
      <circle cx={p0.x} cy={p0.y} r="13" fill={style.fill} stroke={style.dark} strokeWidth="1.6" />
      <circle cx={p0.x + Math.cos(perp) * eyeOff} cy={p0.y + Math.sin(perp) * eyeOff} r="3.1" fill="#fff" />
      <circle cx={p0.x - Math.cos(perp) * eyeOff} cy={p0.y - Math.sin(perp) * eyeOff} r="3.1" fill="#fff" />
      <circle cx={p0.x + Math.cos(perp) * eyeOff + 0.6} cy={p0.y + Math.sin(perp) * eyeOff} r="1.4" fill="#1a1020" />
      <circle cx={p0.x - Math.cos(perp) * eyeOff + 0.6} cy={p0.y - Math.sin(perp) * eyeOff} r="1.4" fill="#1a1020" />
      <path
        d={`M ${p0.x + Math.cos(back) * 12} ${p0.y + Math.sin(back) * 12}
            L ${p0.x + Math.cos(back) * 20} ${p0.y + Math.sin(back) * 20}
            M ${p0.x + Math.cos(back) * 16} ${p0.y + Math.sin(back) * 16}
            L ${p0.x + Math.cos(back - 0.4) * 21} ${p0.y + Math.sin(back - 0.4) * 21}
            M ${p0.x + Math.cos(back) * 16} ${p0.y + Math.sin(back) * 16}
            L ${p0.x + Math.cos(back + 0.4) * 21} ${p0.y + Math.sin(back + 0.4) * 21}`}
        stroke="#e03131"
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx={p3.x} cy={p3.y} r="5" fill={style.dark} />
    </g>
  );
}
